import { Component } from "react"
import PropTypes from "prop-types"
import isEmail from "validator/lib/isEmail"
import { connect } from "react-redux"
import { del } from "shared/lib/api"
import classnames from "classnames"

import {
  inviteUserToOrganization,
  revokeInvitationToOrganization,
  removeUserFromOrganization,
} from "../../../shared/lib/graphql"
import { rejectedXHRPromiseLogger } from "shared/lib/xhr_promise"

import {
  updateOrgMembers,
  updateOrgInvites,
  updateOrgInviteCount,
} from "./actions/index"
import ErrorBoundary from "../../../shared/components/error_boundary"
import Form from "../../../app/components/sdk/form"
import PlatformEditField from "../platform_edit_field/platform_edit_field"
import OrgOwnerField from "./organization_owner_field"
import RetryIcon from "images/icons/icon-retry.svg?raw"
import XIcon from "images/components/x_white.svg?raw"
import displayFlash from "../../scripts/display_flash"
import Modal from "../../../shared/components/modal"

import styles from "./organization_membership.module.scss"

const noop = () => {}

export class OrgMembership extends Component {
  state = {
    createInvitation: false,
    isEmailValid: false,
    invitationStatus: null,
    emailOrLogin: "",
    confirmMemberRemoval: false,
    submitting: false,
  }

  isOwner = () => {
    return this.props.currentUserId === this.props.orgOwner.id
  }

  handleCreateInvitation = e => {
    e.preventDefault()

    const { emailOrLogin } = this.state
    const {
      invites: { pending, expired },
      members,
    } = this.props
    let invitationStatus = ""

    if (pending.find(invite => invite.email === emailOrLogin)) {
      invitationStatus = "pending"
    } else if (expired.find(invite => invite.email === emailOrLogin)) {
      invitationStatus = "expired"
    } else if (members.find(member => member.email === emailOrLogin)) {
      invitationStatus = "converted"
    } else {
      invitationStatus = "new"
      this.sendInvitation(emailOrLogin)
    }
    this.setState({ invitationStatus, createInvitation: true })
  }

  sendInvitation = (email, showConfirmation = false) => {
    inviteUserToOrganization(email.trim(), this.props.orgId.toString())
      .then(response => {
        this.props.dispatch(
          updateOrgInvites({
            pending: response.org.pending_invitations,
            expired: response.org.expired_invitations,
          })
        )
        this.props.dispatch(
          updateOrgInviteCount(response.org.remaining_invitations)
        )
        if (showConfirmation) {
          displayFlash("Invite has been resent")
        }
      })
      .catch(err => console.log(err))
  }

  confirmRemoveUser = (user, status) => {
    if (status === "member") {
      this.setState({ confirmMemberRemoval: true, userPendingRemoval: user })
    } else {
      this.handleRemoveUser(user, status)
    }
  }

  handleRemoveUser = (user = this.state.userPendingRemoval, status) => {
    del(this.props.clearCachePath).then(() => {
      const removalMutation =
        status === "member"
          ? removeUserFromOrganization
          : revokeInvitationToOrganization
      removalMutation(user.id.toString(), this.props.orgId.toString())
        .then(response => {
          // removeUserFromOrganization
          if (response.org_users) {
            this.setState({ confirmMemberRemoval: false, submitting: false })
            this.props.dispatch(updateOrgMembers(response.org_users))
            this.props.dispatch(
              updateOrgInviteCount(this.props.remainingInvites + 1)
            )
          } else {
            // revokeInvitationToOrganization
            this.props.dispatch(updateOrgMembers(response.org.users))
            this.props.dispatch(
              updateOrgInvites({
                pending: response.org.pending_invitations,
                expired: response.org.expired_invitations,
              })
            )
            this.props.dispatch(
              updateOrgInviteCount(response.org.remaining_invitations)
            )
          }
        })
        .catch(
          rejectedXHRPromiseLogger(() => {
            this.setState({ confirmMemberRemoval: false, submitting: false })
            displayFlash("Sorry, that did not work out. Please try again")
          })
        )
    })
  }

  newInvitationDialogue() {
    return (
      <>
        <h3 className={styles["section-margin"]}>Invite new member</h3>
        <p>
          <b>{`You have ${this.props.remainingInvites} remaining invitation${
            this.props.remainingInvites === 1 ? "" : "s"
          }.`}</b>{" "}
          If you need more, please remove an existing member, invitation, or{" "}
          <a
            className={classnames("pop-zendesk-widget", styles["contact-us"])}
            href="mailto:platform-support@ifttt.com"
          >
            contact us
          </a>
          .
        </p>
        {this.props.remainingInvites > 0 && (
          <Form
            onSubmit={this.handleCreateInvitation}
            disabled={!this.state.isEmailValid}
          >
            <input
              type="text"
              onInput={this.handleChangeEmail}
              value={this.state.emailOrLogin}
              placeholder="member@example.com"
              className={styles["form-width"]}
            />
            <div className={styles["form-width"]}>
              <button
                className={classnames("button-tertiary", styles["button-right"])}
                onClick={this.handleCreateInvitation}
                disabled={
                  !this.state.isEmailValid ||
                  this.props.currentUserId !== this.props.orgOwner.id
                }
              >
                Send Invite
              </button>
            </div>
          </Form>
        )}
      </>
    )
  }

  invitationStatusDialogue() {
    const { emailOrLogin, invitationStatus } = this.state
    let cta = ""
    let message = ""
    let onConfirmClick = this.sendInvitation.bind(null, emailOrLogin)
    let buttonAccept = "Okay"
    let buttonDecline = "Back"

    switch (invitationStatus) {
      case "pending":
        message = `There is a pending invitation to ${emailOrLogin}`
        cta =
          "Would you like to cancel the previous invitation and send a new one?"
        buttonAccept = "Yes, send a new invite"
        buttonDecline = "No"
        break
      case "expired":
        message = `An invite sent to ${emailOrLogin} has expired`
        cta = "Would you like to send a new invitation?"
        buttonAccept = "Yes, send a new invite"
        buttonDecline = "No"
        break
      case "converted":
        message = `A user with email ${emailOrLogin} is already a member of your organization.`
        onConfirmClick = noop
        break
      case "new":
        message = `Invite sent to ${emailOrLogin}`
        cta =
          "For security purposes, the invite will expire after one week. You can cancel the invite or resend a new one from this page at any time."
        onConfirmClick = noop
        break
    }

    return (
      <div className={styles["invite-confirmation"]}>
        <p className={styles["center-text"]}>
          <b>{message}</b>
          <br />
          {cta}
        </p>
        <div className={styles["side-by-side"]}>
          {onConfirmClick !== noop && (
            <button
              className={classnames("button-tertiary", styles["button-margin"])}
              onClick={() => this.resetInviteDialogue()}
            >
              {buttonDecline}
            </button>
          )}
          <button
            className={classnames("button-tertiary", styles["button-margin"])}
            onClick={() => this.resetInviteDialogue(onConfirmClick)}
          >
            {buttonAccept}
          </button>
        </div>
      </div>
    )
  }

  resetInviteDialogue = (callback = noop) => {
    this.setState({ createInvitation: false, emailOrLogin: "" }, callback)
  }

  handleChangeEmail = e => {
    this.setState({
      emailOrLogin: e.target.value,
      isEmailValid: isEmail(e.target.value),
    })
  }

  renderUser(user, status) {
    const actions = [
      { callback: () => this.confirmRemoveUser(user, status), icon: XIcon },
    ]
    if (status !== "member")
      actions.unshift({
        callback: () =>
          this.sendInvitation(
            user.email || user.email_for_org_users,
            true /* show confirmation banner */
          ),
        icon: RetryIcon,
      })

    return (
      <PlatformEditField
        key={user.id}
        title={
          status === "member"
            ? `${user.login} ${
                this.isOwner()
                  ? `(
                    ${user.email || user.email_for_org_users})`
                  : ""
              }`
            : `${user.email || user.email_for_org_users} (Invite ${
                status === "pending" ? "sent" : "expired"
              })`
        }
        actions={actions}
        fieldEnabled={this.props.orgOwner.id === this.props.currentUserId}
      />
    )
  }

  onClose = () => {
    this.setState({ confirmMemberRemoval: false })
  }

  renderMemberRemovalDialogue(user) {
    return (
      <Modal
        show={this.state.confirmMemberRemoval}
        closeListener={this.onClose}
        headerText={"Are you sure you want to remove this member?"}
        includeCloseX={false}
      >
        <div>
          <button
            type="submit"
            onClick={() => this.handleRemoveUser(user, "member")}
            className="button-tertiary"
            disabled={this.state.submitting}
          >
            {this.state.submitting ? "Removing..." : "Yes, remove this member"}
          </button>
          <p className={classnames("note", styles.cursor)} onClick={this.onClose}>
            Cancel
          </p>
        </div>
      </Modal>
    )
  }

  render() {
    const { invites } = this.props
    const hasMemberInvitations =
      invites.pending.length + invites.expired.length > 0

    return (
      <ErrorBoundary>
        <header className="page-header">
          <h1>{`${this.props.orgName} Team`}</h1>
        </header>
        <OrgOwnerField
          currentUserId={this.props.currentUserId}
          members={this.props.members}
          orgId={this.props.orgId}
          isOwner={this.isOwner}
        />
        <h3 className={styles["section-margin"]}>Organization members</h3>
        <p>Members can only create, edit, and publish services and Applets.</p>
        {this.props.members
          .filter(member => member.id !== this.props.orgOwner.id)
          .map(member => this.renderUser(member, "member"))}

        <div className={styles.divider} />

        {this.isOwner() && (
          <>
            <>
              {this.state.createInvitation
                ? this.invitationStatusDialogue()
                : this.newInvitationDialogue()}
            </>

            {hasMemberInvitations && (
              <>
                <h3 className={styles["section-margin"]}>Member invitations</h3>
                {this.props.invites.pending.map(user =>
                  this.renderUser(user, "pending")
                )}
                {this.props.invites.expired.map(user =>
                  this.renderUser(user, "expired")
                )}
              </>
            )}
          </>
        )}
        {this.state.confirmMemberRemoval && this.renderMemberRemovalDialogue()}
      </ErrorBoundary>
    )
  }
}

const mapStateToProps = state => {
  return {
    members: state.membership.members,
    invites: state.membership.invites,
    remainingInvites: state.membership.remainingInvites,
    orgName: state.details.orgName,
    orgOwner: state.details.orgOwner,
  }
}

export default connect(mapStateToProps)(OrgMembership)

OrgMembership.propTypes = {
  clearCachePath: PropTypes.string,
  currentUserId: PropTypes.string,
  dispatch: PropTypes.func,
  invites: PropTypes.object,
  members: PropTypes.arrayOf(PropTypes.object),
  orgId: PropTypes.number,
  orgName: PropTypes.string,
  orgOwner: PropTypes.object,
  plansPath: PropTypes.string,
  remainingInvites: PropTypes.number,
}
